import { SETTING_ANCHOR_CLASSES } from './setting-anchor-classes';

import type { PaneOf, PanedPath } from './setting-anchor-routes';
import type { WiredSidebarLabsFlag } from '@/core/AppShell/sidebar-labs-flags';
import type { DashboardAction, DashboardSurfaceOrEveryRole } from '@/lib/dashboard-permissions';
import type { Locale } from '@syncanix/shared';

/**
 * The SHAPE of a setting anchor, and the helper that builds one.
 *
 * Split out of `setting-anchors.ts` when `/experience`'s two cards (Adaptive
 * Compositions D4/D6b) took that file over the ~300-line ceiling
 * (`.claude/rules/architecture.md`). A LEAF: it imports nothing from its parent,
 * so the edge is one-directional and no cycle is possible — the same shape
 * `sidebar-definition.types.ts` takes beside its own list. The parent re-exports
 * `SettingAnchor`, so every consumer still imports from one place.
 *
 * The registry itself deliberately did NOT move. It is the thing this file
 * exists to describe, and a registry split across two files is a registry with
 * two places to forget an entry.
 *
 * Where every individual setting lives, as data (T16; consumed by the ⌘K palette
 * and by the top-bar settings search, which read one shared index).
 *
 * Phase D (ADR-0368) scattered the settings across four surfaces, so "where is
 * retention?" stopped being answerable by looking at one page. This is the single
 * place that knows the answer: each entry carries the DOM id its card renders
 * (`SectionCard`'s `anchorId`), the route, and the tab within that route.
 *
 * It exists as ONE map rather than a string literal in each card because two
 * things must agree — the card that renders the id, and whatever navigates to it.
 * A hardcoded string in each place is a silent 404 waiting for the first rename.
 */

/**
 * One option INSIDE a card that holds several unrelated ones.
 *
 * The card is the base unit of the search, and for most settings that is exactly
 * right — a card is one decision with one heading. A few are not: Account
 * preferences carries theme, control depth, language and numerals, which share
 * nothing but a surface. Landing an operator who typed "language" on the card
 * and letting them find the third group themselves is the failure this exists to
 * fix.
 *
 * A control is never addressable independently of its card: its DOM id is built
 * from the card's, so the two cannot drift apart or be renamed separately.
 */
export interface SettingControl {
  /** Stable slug. The DOM id is `${anchor.id}--${id}`. */
  readonly id: string;
  /**
   * The i18n key for the control's name — deliberately the SAME key the control
   * renders as its own label, so a result reads exactly as the thing it opens.
   */
  readonly labelKey: string;
  /** Extra search terms for this control alone; see {@link SettingAnchor.keywordsKey}. */
  readonly keywordsKey?: string;
  /**
   * The control renders only in Advanced mode, so Simple mode must not offer it
   * (`.claude/rules/ui-design.md` §20 — the search hides, it never reveals).
   */
  readonly requiresAdvanced?: true;
  /**
   * The control renders only in these locales. The Arabic Eastern-numerals
   * opt-in is the live case: it is a no-op everywhere else, and the card does
   * not render it, so offering it would be a jump to a control that is not there.
   */
  readonly localeOnly?: readonly Locale[];
}

/** The routes a setting can live on, and the tab param each uses. */
export interface SettingAnchor {
  /** The DOM id the card renders, and the key used to address it. */
  readonly id: string;
  /** The route the setting lives on. */
  readonly path: string;
  /**
   * The PANE of its page that reveals it — a tab on `/settings/workspace`, a
   * Design Studio target on `/widget`.
   *
   * WHICH query param carries it is a fact about the ROUTE rather than about the
   * setting, so it lives once per route in `SETTING_ANCHOR_ROUTES` instead of
   * thirty-one times here.
   */
  readonly pane?: string;
  /**
   * The i18n key for the setting's name — deliberately the SAME key its card
   * renders as its heading, so a search result reads exactly as the card it
   * opens (and adding a setting costs no new translation).
   */
  readonly labelKey: string;
  /**
   * The i18n key for this setting's search KEYWORDS — a bag of the words an
   * operator actually types, which are routinely not the words on screen.
   * Somebody looking for "how long do we keep chats" types "delete after days";
   * the card is headed "Conversation retention".
   *
   * REQUIRED, and that is the point: a bag is invisible when it is missing —
   * the setting still appears when you type its exact heading, so nothing looks
   * broken, and the operator who does not know the heading finds nothing. The
   * compiler asks for one on every new anchor; the coverage test checks each key
   * resolves in all six catalogs (`settings-search-coverage.convention.test.ts`).
   *
   * Same mechanism as the Design Studio's own `studio.search.*` bags, which is
   * the surface this search was modelled on.
   */
  readonly keywordsKey: string;
  /** The dashboard surface + action the server gates this setting on. */
  readonly surface: DashboardSurfaceOrEveryRole;
  readonly action: DashboardAction;
  /**
   * Extra conditions the CARD applies that the surface matrix cannot express.
   * `owner` — the card renders for an owner only (transfer / delete).
   * `nonPersonal` — refused on a personal workspace (leave / transfer / delete).
   *
   * They exist so a search never offers a jump that lands on a card the caller
   * cannot see: a dead end is worse than an absent result.
   */
  readonly requiresOwner?: true;
  readonly requiresNonPersonal?: true;
  /**
   * The whole setting renders only in Advanced mode, so Simple must not offer it
   * (`.claude/rules/ui-design.md` §20 — the search hides, it never reveals).
   *
   * The same field {@link SettingControl} carries, raised to the card: the
   * Studio's Component styling pane is advanced-only in its entirety, not one
   * control within something visible.
   */
  readonly requiresAdvanced?: true;
  /**
   * The Labs flag that has to be ON for this setting's PAGE to exist at all.
   *
   * The same flag the sidebar row carries, read through the same wired-flag
   * union, so the two cannot disagree. It closes a real hole: the three
   * `/experience` anchors were offered by ⌘K while the sidebar hid `/experience`
   * and the route itself 404s, so the one surface that promised to find a
   * setting was the one place a flagged-off page was still reachable.
   *
   * Fail-CLOSED, like the sidebar's gate: hidden while the flag loads, hidden on
   * an error, hidden for an id the server does not list.
   */
  readonly labsFlag?: WiredSidebarLabsFlag;
  /** Individually addressable options inside the card — see {@link SettingControl}. */
  readonly controls?: readonly SettingControl[];
}

interface AnchorInitBase {
  /** See {@link SettingAnchor.keywordsKey}. Required on every anchor. */
  readonly keywordsKey: string;
  readonly requiresOwner?: true;
  readonly requiresNonPersonal?: true;
  readonly requiresAdvanced?: true;
  readonly labsFlag?: WiredSidebarLabsFlag;
  readonly controls?: readonly SettingControl[];
}

/**
 * `pane` is legal only on a route that HAS panes, and only with a value that
 * route accepts.
 *
 * A mistyped pane used to be invisible: the href simply omitted the query, the
 * page opened on its own default, and the operator landed on the wrong pane with
 * nothing to say so. That is exactly how the two `/widget` anchors came to land
 * on nothing at all. The compiler answers it now, on the file people add to at
 * five in the afternoon.
 */
type AnchorInit<P extends string> = AnchorInitBase &
  (P extends PanedPath ? { readonly pane?: PaneOf<P> } : { readonly pane?: never });

export function anchor<P extends string>(
  id: string,
  path: P,
  labelKey: string,
  surface: DashboardSurfaceOrEveryRole,
  action: DashboardAction,
  init: AnchorInit<P>,
): SettingAnchor {
  // The one cast in the module: an unresolved conditional over an unbound `P` is
  // not spreadable. Every caller has already been checked against it above.
  const base = init as AnchorInitBase & { readonly pane?: string };
  return { id, path, labelKey, surface, action, ...base };
}

/**
 * Re-exported so a registry file still names it from one import. It MOVED to
 * `setting-anchor-routes.ts`, which is the module that now knows what a route's
 * panes are — a route constant in a shape file was always slightly off.
 */
export { SETTINGS } from './setting-anchor-routes';

/**
 * The props a CARD renders so a search result can land on it, for a card that is
 * not a `SectionCard`.
 *
 * `SectionCard` takes an `anchorId` and does this itself, which covers most of
 * the registry. The `/limits` surfaces do not use it — the tab body already
 * draws the outline, so its regions are a heading plus a bordered list rather
 * than cards (`ui-design.md` §0, box-in-box) — and they still have to be
 * landable.
 */
export function settingAnchorProps(anchorId: string): {
  readonly id: string;
  readonly tabIndex: -1;
  readonly className: string;
} {
  return { id: anchorId, tabIndex: -1, className: `scroll-mt-24 ${SETTING_ANCHOR_CLASSES}` };
}

/**
 * The props a control renders so a search result can land on it.
 *
 * The card-level twin of `SectionCard`'s `anchorId` handling, spread onto
 * whatever element the control's label names — usually the `radiogroup` or the
 * field wrapper. Same three parts and the same reasons: a stable id so the jump
 * has a target, `tabIndex={-1}` so it is focusable by script and never by Tab,
 * and the shared classes so the focus ring and the landing mark are visible when
 * it happens.
 */
export function settingControlAnchorProps(
  anchorId: string,
  controlId: string,
): { readonly id: string; readonly tabIndex: -1; readonly className: string } {
  return {
    id: `${anchorId}--${controlId}`,
    tabIndex: -1,
    className: `scroll-mt-24 ${SETTING_ANCHOR_CLASSES}`,
  };
}
